import {
  ETemplateStyles,
  ObjectStyle,
  ParagraphStyle
} from '../types/templates';

/**
 * Converts an ObjectStyle to CSS property-value pairs
 */
const objectStyleToCSSProperties = (
  objectStyle: ObjectStyle
): Record<string, string> => {
  const cssProps: Record<string, string> = {};

  const {
    backgroundColor,
    borderColor,
    strokeWeight,
    cornerRadius,
    insetSpacing
  } = objectStyle;
  if (backgroundColor) {
    cssProps['background-color'] = backgroundColor;
  }

  if (borderColor && strokeWeight) {
    cssProps.border = `${strokeWeight}pt solid ${borderColor}`;
  }

  if (cornerRadius) {
    cssProps['border-radius'] = `${cornerRadius}pt`;
  }

  if (insetSpacing) {
    const { top, right, bottom, left } = insetSpacing;
    const paddingValues = [
      top ? `${top}pt` : '0',
      right ? `${right}pt` : '0',
      bottom ? `${bottom}pt` : '0',
      left ? `${left}pt` : '0'
    ];
    cssProps.padding = paddingValues.join(' ');
  }

  return cssProps;
};

/**
 * Converts objectStyles from ETemplateStyles into CSS rules string with one class per object style.
 * Each object style becomes a CSS class named after the style with spaces replaced by hyphens.
 * @returns CSS string with class definitions, or empty string if no objectStyles
 *
 * @example
 * ```typescript
 * const templateStyles: ETemplateStyles = {
 *   objectStyles: {
 *     "Header Box": {
 *       name: "Header Box",
 *       backgroundColor: "#f0f0f0",
 *       cornerRadius: 5,
 *       strokeWeight: 1,
 *       borderColor: "#333333",
 *       insetSpacing: { top: 10, right: 10, bottom: 10, left: 10 },
 *       // ... other properties
 *     }
 *   }
 * };
 *
 * const css = generateObjectStylesCSS(templateStyles);
 * // Returns:
 * // .header-box {
 * //   background-color: #f0f0f0;
 * //   border-radius: 5pt;
 * //   border: 1pt solid #333333;
 * //   padding: 10pt 10pt 10pt 10pt;
 * // }
 * ```
 */
export const generateObjectStylesCSS = (
  templateStyles: ETemplateStyles
): string => {
  if (!templateStyles.objectStyles) {
    return '';
  }

  const cssRules: string[] = [];

  for (const [styleName, objectStyle] of Object.entries(
    templateStyles.objectStyles
  )) {
    // Convert style name to valid CSS class name
    const className = convertStyleNameToClassName(styleName);

    // Skip if className is empty after sanitization
    if (!className) continue;

    const cssProperties = objectStyleToCSSProperties(objectStyle);

    // Only create a rule if there are CSS properties to set
    if (Object.keys(cssProperties).length > 0) {
      const propertyStrings = Object.entries(cssProperties)
        .map(([prop, value]) => `  ${prop}: ${value};`)
        .join('\n');

      cssRules.push(`.${className} {\n${propertyStrings}\n}`);
    }
  }

  return cssRules.join('\n\n');
};

export const adobeInDesignJustificationToCSS = (
  justification: string | undefined
): Record<string, string> => {
  switch (justification) {
    case 'LeftAlign':
      return { 'text-align': 'left' };
    case 'CenterAlign':
      return { 'text-align': 'center' };
    case 'RightAlign':
      return { 'text-align': 'right' };
    case 'RightJustified':
      return { 'text-align': 'justify', 'text-align-last': 'right' };
    case 'LeftJustified':
      return { 'text-align': 'justify', 'text-align-last': 'left' };
    case 'CenterJustified':
      return { 'text-align': 'justify', 'text-align-last': 'center' };
    case 'FullyJustified':
      return { 'text-align': 'justify' };
    default:
      return { 'text-align': 'justify' };
  }
};

/**
 * Converts a ParagraphStyle to CSS property-value pairs
 */
const paragraphStyleToCSSProperties = (
  paragraphStyle: ParagraphStyle
): Record<string, string> => {
  const cssProps: Record<string, string> = {};

  const {
    fontFamily,
    fontSize,
    fontWeight,
    leading,
    tracking,
    justification,
    underline,
    leftIndent,
    rightIndent,
    firstLineIndent,
    spaceBefore,
    spaceAfter,
    fontColor
  } = paragraphStyle;

  if (fontFamily) {
    cssProps['font-family'] = `'${fontFamily}', sans-serif`;
  }

  if (fontSize) {
    cssProps['font-size'] = `${fontSize}pt`;
  }

  if (fontWeight && fontWeight !== 'Regular') {
    // Map InDesign font weights to CSS
    const weightMap: Record<string, string> = {
      Bold: 'bold',
      Light: '300',
      Medium: '500',
      Heavy: '800',
      Black: '900'
    };
    cssProps['font-weight'] = weightMap[fontWeight] || fontWeight.toLowerCase();
  }

  if (leading) {
    cssProps['line-height'] = `${leading}pt`;
  }

  if (tracking) {
    // Convert InDesign tracking (thousandths of an em) to CSS em units
    const trackingEm = tracking / 1000;
    cssProps['letter-spacing'] = `${trackingEm}em`;
  }

  if (justification) {
    // Use the shared justification helper
    const justificationProps = adobeInDesignJustificationToCSS(justification);
    Object.assign(cssProps, justificationProps);
  }

  if (underline) {
    cssProps['text-decoration'] = 'underline';
  }

  if (leftIndent) {
    cssProps['margin-left'] = `${leftIndent}pt`;
  }

  if (rightIndent) {
    cssProps['margin-right'] = `${rightIndent}pt`;
  }

  if (firstLineIndent) {
    cssProps['text-indent'] = `${firstLineIndent}pt`;
  }

  cssProps['margin-top'] = `${spaceBefore || 0}pt`;
  cssProps['margin-bottom'] = `${spaceAfter || 0}pt`;

  if (fontColor) {
    cssProps.color = fontColor;
  }

  // TODO: Remove this, only applied to retain snapshot style and can be deprecated
  cssProps['word-spacing'] = '0.1em';

  return cssProps;
};

/**
 * Converts paragraphStyles from ETemplateStyles into CSS rules string with one class per paragraph style.
 * Each paragraph style becomes a CSS class named after the style with spaces replaced by hyphens.
 * @returns CSS string with class definitions, or empty string if no paragraphStyles
 *
 * @example
 * ```typescript
 * const templateStyles: ETemplateStyles = {
 *   paragraphStyles: {
 *     "Header Style": {
 *       name: "Header Style",
 *       fontFamily: "Arial",
 *       fontSize: 14,
 *       fontWeight: "Bold",
 *       justification: "CenterAlign",
 *       spaceBefore: 12,
 *       spaceAfter: 6,
 *       // ... other properties
 *     }
 *   }
 * };
 *
 * const css = generateParagraphStylesCSS(templateStyles);
 * // Returns:
 * // .header-style {
 * //   font-family: 'Arial', sans-serif;
 * //   font-size: 14pt;
 * //   font-weight: bold;
 * //   text-align: center;
 * //   margin-top: 12pt;
 * //   margin-bottom: 6pt;
 * // }
 * ```
 */
export const generateParagraphStylesCSS = (
  templateStyles: ETemplateStyles
): string => {
  if (!templateStyles.paragraphStyles) {
    return '';
  }

  const cssRules: string[] = [];

  for (const [styleName, paragraphStyle] of Object.entries(
    templateStyles.paragraphStyles
  )) {
    // Convert style name to valid CSS class name
    const className = convertStyleNameToClassName(styleName);

    // Skip if className is empty after sanitization
    if (!className) continue;

    const cssProperties = paragraphStyleToCSSProperties(paragraphStyle);

    // Only create a rule if there are CSS properties to set
    if (Object.keys(cssProperties).length > 0) {
      const propertyStrings = Object.entries(cssProperties)
        .map(([prop, value]) => `  ${prop}: ${value};`)
        .join('\n');

      cssRules.push(`.${className} {\n${propertyStrings}\n}`);
    }
  }

  return cssRules.join('\n\n');
};

export function convertStyleNameToClassName(
  styleName: string | undefined
): string | undefined {
  if (!styleName) return undefined;
  return styleName
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}
